import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { LoginForm } from '@/features/auth/components/LoginForm';
import { useAuth } from '@/providers';

// Abstract art component for the magazine covers
const AbstractCoverArt = ({ variant }: { variant: 1 | 2 | 3 }) => {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-sm bg-[#F4F2F2]">
      {variant === 1 && (
        <>
          <div className="absolute top-10 left-[-20%] h-32 w-32 rounded-full bg-[#E94E1B]/80 mix-blend-multiply" />
          <div className="absolute bottom-10 right-[-10%] h-40 w-40 rounded-full bg-[#2E2A23]/20" />
          <div className="absolute top-[40%] left-[20%] h-24 w-4 bg-[#2E2A23] rotate-12" />
          <div className="absolute bottom-20 left-10 h-16 w-16 bg-[#CAC7C7] rounded-t-full" />
        </>
      )}
      {variant === 2 && (
        <>
          <div className="absolute top-1/2 left-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 border-[20px] border-[#E94E1B]/40 rounded-full" />
          <div className="absolute top-10 right-4 h-20 w-12 bg-[#2E2A23] rounded-t-xl opacity-80" />
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-[#CAC7C7]/50 transform -skew-y-6 origin-bottom-left" />
        </>
      )}
      {variant === 3 && (
        <>
          <div className="absolute inset-x-8 top-12 bottom-24 border-2 border-[#2E2A23]/20" />
          <div className="absolute top-20 right-[-10%] h-24 w-24 rounded-full bg-[#E94E1B]/60" />
          <div className="absolute bottom-12 left-8 h-20 w-32 bg-[#2E2A23]/40" />
          <div className="absolute top-1/3 left-1/3 w-2 h-24 bg-[#2E2A23] rotate-45" />
        </>
      )}
    </div>
  );
};

const MagazineCard = ({
  title = 'Newsletter',
  variant = 1,
  className = '',
  style = {},
}: {
  title?: string;
  variant?: 1 | 2 | 3;
  className?: string;
  style?: React.CSSProperties;
}) => {
  return (
    <div
      className={`relative flex flex-col bg-[#F4F2F2] shadow-2xl ${className}`}
      style={{
        width: '240px',
        height: '320px',
        ...style,
      }}
    >
      <div className="relative h-full w-full p-3 flex flex-col items-center">
        <h3 className="font-serif text-lg text-[#2E2A23] tracking-tight mb-3 z-10">{title}</h3>
        <div className="relative flex-1 w-full mb-4">
          <AbstractCoverArt variant={variant} />
        </div>
        <div className="w-full space-y-1 opacity-40">
          <div className="h-0.5 w-full bg-[#2E2A23] rounded-full" />
          <div className="h-0.5 w-[90%] bg-[#2E2A23] rounded-full" />
          <div className="h-0.5 w-[60%] bg-[#2E2A23] rounded-full" />
        </div>
      </div>
    </div>
  );
};

export function LoginPage() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/feed';
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen bg-[#F4F2F2] flex flex-col">
      {/* Header */}
      <header className="w-full px-8 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="font-serif text-2xl text-[#2E2A23] hover:text-[#E94E1B] transition-colors"
        >
          Newsletter
        </Link>
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-[#888185] hover:text-[#E94E1B] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left side - Magazine Covers */}
        <div className="hidden lg:flex lg:flex-1 items-center justify-center p-12">
          <div className="relative h-[500px] w-[500px] flex items-center justify-center">
            {/* Magazine 3 (Back) */}
            <motion.div
              initial={{ opacity: 0, x: 30, rotate: 8 }}
              animate={{ opacity: 1, x: 100, y: -40, rotate: 12 }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
              className="absolute z-10"
            >
              <MagazineCard
                title="Newsletter"
                variant={3}
                className="opacity-90 blur-[0.3px] scale-90"
              />
            </motion.div>

            {/* Magazine 2 (Middle) */}
            <motion.div
              initial={{ opacity: 0, x: 10, rotate: -3 }}
              animate={{ opacity: 1, x: -80, y: 50, rotate: -12 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="absolute z-20"
            >
              <MagazineCard
                title="Newsletter"
                variant={2}
                className="opacity-95 blur-[0.2px] scale-95"
              />
            </motion.div>

            {/* Magazine 1 (Front) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0, rotate: -3 }}
              transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
              className="relative z-30"
            >
              <MagazineCard
                title="Newsletter"
                variant={1}
                className="shadow-[0_15px_40px_rgba(0,0,0,0.12)]"
              />
            </motion.div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full max-w-md"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <LoginForm initialMode={initialMode} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
